import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Text,
  Alert,
  AlertIcon,
  Icon,
  Badge,
  Divider,
  Switch,
} from '@chakra-ui/react';
import { FiUserPlus, FiAlertTriangle } from 'react-icons/fi';
import { Select, CreatableSelect } from 'chakra-react-select';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import { applyTags } from '../../utils/crmTags';
import { normalizeEmail, normalizePhone } from '../../utils/contactNormalization';

/**
 * Quick Add Contact Modal
 * 
 * Fast contact creation with minimal required fields.
 * 
 * Features:
 * - Duplicate detection
 * - Multi-type selection
 * - "Add & Open" or "Add & Close" options
 */
export default function QuickAddContact({ 
  isOpen, 
  onClose, 
  onContactCreated,
  prefilledData = {},
}) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const toast = useToast();
  const { contactTypes: configContactTypes, getVisibleContactTags } = useCrmSettings();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const availableTags = getVisibleContactTags(currentUser?.uid, isCRMAdmin) || [];

  const [firstName, setFirstName] = useState(prefilledData.firstName || '');
  const [lastName, setLastName] = useState(prefilledData.lastName || '');
  const [email, setEmail] = useState(prefilledData.email || '');
  const [phone, setPhone] = useState(prefilledData.phone || '');
  const [selectedTypes, setSelectedTypes] = useState(
    prefilledData.contactTypes || [{ value: 'borrower', label: 'Borrower' }]
  );
  const [selectedTags, setSelectedTags] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Admin-only: create as a company record (visible to every CRM admin)
  const [isCompanyRecord, setIsCompanyRecord] = useState(false);

  // Candidates are company records by policy: when an admin tags the new
  // contact as a candidate, the scope switch locks on (see handleSubmit).
  const hasCandidateType = selectedTypes.some(t => t?.value === 'candidate');
  useEffect(() => {
    if (isCRMAdmin && hasCandidateType) setIsCompanyRecord(true);
  }, [isCRMAdmin, hasCandidateType]);

  const handleSubmit = async (openAfterCreate = false, { skipDuplicateCheck = false } = {}) => {
    // Validation
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: 'Missing required fields',
        description: 'First name and last name are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!email.trim() && !phone.trim()) {
      toast({
        title: 'Missing contact info',
        description: 'Either email or phone is required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (selectedTypes.length === 0) {
      toast({
        title: 'Missing contact type',
        description: 'Please select at least one contact type',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    setDuplicateWarning(null);

    // Store the same normalized shape the import pipeline writes, or future
    // dedupe between manual adds and imports breaks.
    const normalizedEmail = normalizeEmail(email);
    const normalizedPhone = normalizePhone(phone);

    try {
      // Check for duplicates by email OR phone: same owner, plus company
      // records (which the new contact could silently duplicate).
      if (!skipDuplicateCheck) {
        let existing = null;
        let matchedOn = null;
        const dupVisibilityFilter = `owner_id.eq.${currentUser.uid},record_scope.eq.company`;

        if (normalizedEmail) {
          const { data: emailMatches } = await supabase
            .from('crm_contacts')
            .select('id, first_name, last_name, email, phone, owner_id, record_scope')
            .ilike('email', normalizedEmail)
            .or(dupVisibilityFilter)
            .limit(1);
          if (emailMatches && emailMatches.length > 0) {
            existing = emailMatches[0];
            matchedOn = 'email';
          }
        }

        if (!existing && normalizedPhone) {
          const { data: phoneMatches } = await supabase
            .from('crm_contacts')
            .select('id, first_name, last_name, email, phone, owner_id, record_scope')
            .eq('phone', normalizedPhone)
            .or(dupVisibilityFilter)
            .limit(1);
          if (phoneMatches && phoneMatches.length > 0) {
            existing = phoneMatches[0];
            matchedOn = 'phone';
          }
        }

        if (existing) {
          setDuplicateWarning({
            id: existing.id,
            firstName: existing.first_name,
            lastName: existing.last_name,
            email: existing.email,
            phone: existing.phone,
            matchedOn,
            isCompanyRecord: existing.record_scope === 'company' && existing.owner_id !== currentUser.uid,
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Create contact in Supabase (nulls, not empty strings)
      const contactData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: normalizedEmail || null,
        phone: normalizedPhone || null,
        contact_types: selectedTypes.map(t => t.value),
        primary_contact_type: selectedTypes[0]?.value || null,
        source: prefilledData.source || 'manual',
        owner_id: currentUser.uid,
        is_active: true,
        is_favorite: false,
        do_not_contact: false,
        // Admin-only company records; everyone else keeps the column default.
        // Candidate-typed contacts created by an admin are always company.
        ...(isCRMAdmin && (isCompanyRecord || hasCandidateType) ? { record_scope: 'company' } : {}),
      };

      const { data: newContact, error } = await supabase
        .from('crm_contacts')
        .insert(contactData)
        .select()
        .single();

      if (error) throw error;

      // Tags are applied after the insert, not as part of it: the audit trigger
      // only emits tag events on UPDATE, and this path also registers unknown
      // tags and grants the owner filter visibility.
      let createdTags = [];
      if (selectedTags.length > 0) {
        try {
          createdTags = await applyTags({
            contactId: newContact.id,
            add: selectedTags.map(t => t.value),
            actorId: currentUser.uid,
          }) || [];
        } catch (tagErr) {
          console.error('Error applying tags to new contact:', tagErr);
          toast({
            title: 'Contact created, but tags failed',
            description: tagErr.message,
            status: 'warning',
            duration: 6000,
          });
        }
      }

      // No client-side automation call needed: the crm_contacts audit trigger
      // emits contact.created on INSERT, which enrolls campaigns exactly once.
      // (A client-side handleAutomationTriggers call here used to double-fire.)

      toast({
        title: 'Contact created',
        description: `${firstName} ${lastName} has been added to your contacts`,
        status: 'success',
        duration: 3000,
      });

      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setSelectedTypes([{ value: 'borrower', label: 'Borrower' }]);
      setSelectedTags([]);
      setIsCompanyRecord(false);

      if (onContactCreated) {
        onContactCreated({
          id: newContact.id,
          firstName: newContact.first_name,
          lastName: newContact.last_name,
          email: newContact.email,
          phone: newContact.phone,
          contactTypes: newContact.contact_types,
          primaryContactType: newContact.primary_contact_type,
          tags: createdTags,
          source: newContact.source,
          isActive: newContact.is_active,
          isFavorite: newContact.is_favorite,
          ownerId: newContact.owner_id,
          recordScope: newContact.record_scope || 'user',
        });
      }

      if (openAfterCreate) {
        toast({
          title: 'Opening contact...',
          status: 'info',
          duration: 1000,
        });
      }

      onClose();
    } catch (error) {
      console.error('Error creating contact:', error);
      toast({
        title: 'Error creating contact',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverrideDuplicate = () => {
    // User reviewed the duplicate warning and wants the contact anyway
    handleSubmit(false, { skipDuplicateCheck: true });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiUserPlus} />
            <Text>Quick Add Contact</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Duplicate Warning */}
            {duplicateWarning && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon as={FiAlertTriangle} />
                <VStack align="flex-start" spacing={2} flex={1}>
                  <Text fontSize="sm" fontWeight="semibold">
                    Potential Duplicate Found
                  </Text>
                  <Text fontSize="xs">
                    {duplicateWarning.isCompanyRecord
                      ? `A company record for ${duplicateWarning.firstName} ${duplicateWarning.lastName} already exists with`
                      : `You already have ${duplicateWarning.firstName} ${duplicateWarning.lastName} with`}
                    {' '}the same {duplicateWarning.matchedOn === 'email' ? 'email' : 'phone'}
                    {' '}({duplicateWarning.matchedOn === 'email' ? duplicateWarning.email : duplicateWarning.phone}).
                  </Text>
                  <Button
                    size="xs"
                    colorScheme="orange"
                    variant="outline"
                    onClick={handleOverrideDuplicate}
                    isLoading={isSubmitting}
                  >
                    Create Anyway
                  </Button>
                </VStack>
              </Alert>
            )}

            {/* Basic Info */}
            <HStack spacing={3} data-tour="qac-name">
              <FormControl isRequired>
                <FormLabel fontSize="sm">First Name</FormLabel>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  autoFocus
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Last Name</FormLabel>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                />
              </FormControl>
            </HStack>

            {/* Contact Info: either email or phone is required, not both, so
                neither field carries the misleading required asterisk */}
            <Box data-tour="qac-contact-info">
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john.doe@example.com"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Phone</FormLabel>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </FormControl>

                <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                  At least one of email or phone is required
                </Text>
              </VStack>
            </Box>

            <Divider />

            <Box data-tour="qac-type-tags">
              <VStack spacing={4} align="stretch">
                {/* Contact Types */}
                <FormControl isRequired>
                  <FormLabel fontSize="sm">
                    Contact Type(s)
                    <Badge ml={2} colorScheme="blue" fontSize="xs">
                      Can select multiple
                    </Badge>
                  </FormLabel>
                  <Select
                    isMulti
                    value={selectedTypes}
                    onChange={setSelectedTypes}
                    options={configContactTypes.map(type => ({
                      value: type.value,
                      label: type.label,
                      colorScheme: type.color,
                    }))}
                    placeholder="Select contact type(s)..."
                  />
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    First selected type will be the primary type
                  </Text>
                </FormControl>

                {/* Tags (Optional) */}
                <FormControl>
                  <FormLabel fontSize="sm">Tags (Optional)</FormLabel>
                  <CreatableSelect
                    isMulti
                    value={selectedTags}
                    onChange={(options) => setSelectedTags(options || [])}
                    options={availableTags.map(tag => ({
                      value: tag.value,
                      label: tag.label,
                    }))}
                    placeholder="Add tags..."
                    formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                  />
                </FormControl>

                {/* Admin-only: company record scope. Locked on for candidates —
                    candidate contacts are always company records. */}
                {isCRMAdmin && (
                  <FormControl display="flex" alignItems="center">
                    <Switch
                      size="sm"
                      colorScheme="purple"
                      isChecked={isCompanyRecord || hasCandidateType}
                      isDisabled={hasCandidateType}
                      onChange={(e) => setIsCompanyRecord(e.target.checked)}
                      mr={2}
                    />
                    <FormLabel fontSize="sm" mb={0} mr={2}>
                      Company record
                    </FormLabel>
                    <Badge colorScheme="purple" fontSize="xs">
                      {hasCandidateType ? 'candidates are always company records' : 'visible to all CRM admins'}
                    </Badge>
                  </FormControl>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter data-tour="qac-actions">
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            variant="outline"
            mr={2}
            onClick={() => handleSubmit(false)}
            isLoading={isSubmitting}
            loadingText="Creating..."
          >
            Add Contact
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => handleSubmit(true)}
            isLoading={isSubmitting}
            loadingText="Creating..."
          >
            Add & Open
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

